<script setup>
// ============================================================================
// BonBoZ: BẢNG GẮN THẺ cho ĐIỆN THOẠI — bảng trượt lên từ đáy màn.
//
// Trên máy tính, thẻ nằm thành lưới ngay dưới khung chat (ComposerTagGrid).
// Trên điện thoại lưới đó ăn ba hàng của một màn hình vốn đã chật, và Pancake
// bản điện thoại cũng không để ở đó: họ đưa vào nút thẻ trên thanh tiêu đề,
// bấm ra một bảng trượt có ô tìm kiếm và danh sách thẻ để tick.
//
// Cùng bộ luật với lưới (xem `composables/useTheHoiThoai.js`): cùng bộ thẻ
// theo trang, cùng cách ghi lạc quan, cùng màu chip.
//
// Bấm là gắn/gỡ NGAY, không có nút Lưu: đây là thao tác lặp cả ngày của trực
// page, thêm một bước xác nhận là thêm một lần chạm cho mỗi thẻ.
// ============================================================================
import { computed, nextTick, ref, watch } from 'vue';
import {
  bienCssThe,
  useTheHoiThoai,
} from 'dashboard/composables/useTheHoiThoai';

const props = defineProps({
  dangMo: { type: Boolean, default: false },
});
const emit = defineEmits(['dong']);

const { labelsHien, isActive, toggle } = useTheHoiThoai();

const tuKhoa = ref('');
const oTim = ref(null);

// Bỏ dấu để gõ "dv mui" cũng ra "DV_MŨI": người trực gõ nhanh một tay, ít khi
// bỏ dấu đúng. Gạch dưới và gạch ngang coi như khoảng trắng vì tên thẻ hay
// dùng chúng (`rc_an_toan`, `cần-hr`).
const chuanHoa = chuoi =>
  (chuoi || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();

const theLoc = computed(() => {
  const tim = chuanHoa(tuKhoa.value);
  if (!tim) return labelsHien.value;
  return labelsHien.value.filter(l => chuanHoa(l.title).includes(tim));
});

// Mở ra là ô tìm kiếm KHÔNG tự nhận con trỏ: bàn phím bật lên sẽ che mất nửa
// danh sách, mà phần lớn lần dùng là tick một thẻ quen thuộc chứ không phải
// tìm. Ai cần tìm thì chạm vào ô.
watch(
  () => props.dangMo,
  async mo => {
    if (mo) {
      tuKhoa.value = '';
      await nextTick();
      oTim.value?.blur();
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200"
      leave-to-class="opacity-0"
    >
      <div
        v-if="dangMo"
        class="fixed inset-0 z-50 bg-black/40"
        @click="emit('dong')"
      />
    </Transition>
    <Transition
      enter-active-class="transition-transform duration-250 ease-out"
      enter-from-class="translate-y-full"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-to-class="translate-y-full"
    >
      <div
        v-if="dangMo"
        class="flex fixed inset-x-0 bottom-0 z-50 flex-col rounded-t-2xl shadow-2xl bg-n-solid-1 max-h-[82vh]"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('BONBOZ.BANG_THE.TIEU_DE')"
        @keydown.esc="emit('dong')"
      >
        <!-- Thanh kéo: dấu hiệu quen thuộc của bảng trượt trên điện thoại, chỉ
             để trang trí — đóng bằng cách chạm ra ngoài hoặc nút Xong. -->
        <div class="flex justify-center pt-2 pb-1 shrink-0">
          <div class="h-1 rounded-full w-9 bg-n-slate-6" aria-hidden="true" />
        </div>

        <div class="flex gap-2 items-center px-4 pb-2 shrink-0">
          <span class="w-12 shrink-0" aria-hidden="true" />
          <h3
            class="flex-1 text-base font-medium text-center truncate text-n-slate-12"
          >
            {{ $t('BONBOZ.BANG_THE.TIEU_DE') }}
          </h3>
          <button
            type="button"
            class="w-12 text-sm font-medium shrink-0 text-n-brand"
            @click="emit('dong')"
          >
            {{ $t('BONBOZ.BANG_THE.XONG') }}
          </button>
        </div>

        <div class="px-4 pb-3 shrink-0">
          <input
            ref="oTim"
            v-model="tuKhoa"
            type="search"
            class="!h-10 !mb-0 w-full !rounded-xl"
            :placeholder="$t('BONBOZ.BANG_THE.TIM')"
          />
        </div>

        <!-- pb an toan: dem duoi de hang cuoi khong nam duoi thanh dieu huong
             cua he dieu hanh tren dien thoai khong co nut cung. -->
        <div
          class="overflow-y-auto flex-1 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] bonboz-cuon-manh"
        >
          <p
            v-if="!theLoc.length"
            class="py-8 text-sm text-center text-n-slate-11"
          >
            {{ $t('BONBOZ.BANG_THE.KHONG_CO') }}
          </p>
          <button
            v-for="label in theLoc"
            :key="label.id"
            type="button"
            role="checkbox"
            :aria-checked="isActive(label.title)"
            class="flex gap-3 items-center py-2.5 w-full text-left border-b border-n-weak last:border-b-0"
            @click="toggle(label)"
          >
            <span
              class="grid place-items-center rounded-full border-2 size-6 shrink-0"
              :class="
                isActive(label.title)
                  ? 'bg-n-brand border-n-brand text-white'
                  : 'border-n-slate-6 text-transparent'
              "
              aria-hidden="true"
            >
              <span class="i-lucide-check size-4" />
            </span>
            <span
              class="bonboz-chip min-w-0 max-w-full truncate rounded-full px-3 py-1 text-[13px] font-medium leading-5"
              :class="
                isActive(label.title) ? 'bonboz-chip-bat' : 'bonboz-chip-tat'
              "
              :style="bienCssThe(label.color)"
            >
              {{ label.title }}
            </span>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
